import type { RefObject, KeyboardEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  XMarkIcon,
  Bars3Icon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";
import { QuestionMarkCircleIcon, BookOpenIcon } from "@heroicons/react/24/solid";
import { Loader } from "../loader.js";
import { AiAvatar, TypingIndicator, MessageBubble } from "./chat-message.js";
import { CHAT_MODE_INFO, type ChatMessage, type ChatMode } from "./helpers.js";

export interface ChatViewProps {
  messages: ChatMessage[];
  mode: ChatMode | null;
  showInput: boolean;
  isSending: boolean;
  isResumingConversation: boolean;
  inputText: string;
  isSendButtonDisabled: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  messagesEndRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLTextAreaElement>;
  conversationTitle: string | null;
  onShowHistory: () => void;
  onNewChat: () => void;
  onSelectMode: (mode: ChatMode) => void;
  onToggleExpanded: () => void;
  onClose: () => void;
  onInputChange: (text: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
}

export function ChatView({
  messages,
  mode,
  showInput,
  isSending,
  isResumingConversation,
  inputText,
  isSendButtonDisabled,
  isMobile,
  isExpanded,
  messagesEndRef,
  inputRef,
  conversationTitle,
  onShowHistory,
  onNewChat,
  onSelectMode,
  onToggleExpanded,
  onClose,
  onInputChange,
  onKeyDown,
  onSend,
}: ChatViewProps) {
  return (
    <>
      {/* Chat header */}
      <div className="flex items-center justify-between border-b border-gray px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onShowHistory}
            className="shrink-0 rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
            aria-label="Chat history">
            <Bars3Icon className="h-5 w-5" />
          </button>
          {conversationTitle && (
            <span title={conversationTitle} className="truncate text-sm font-medium text-midnight">
              {conversationTitle}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onNewChat}
            className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
            aria-label="New chat">
            <PencilSquareIcon className="h-5 w-5" />
          </button>
          {!isMobile && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
              aria-label={isExpanded ? "Collapse" : "Expand"}>
              {isExpanded ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
              aria-label="Close chat">
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-lac-leman-4 px-4 py-4">
        {isResumingConversation ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader size="small" delayMs={null} />
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <AiAvatar />
              <div className="max-w-[75%] rounded-2xl rounded-bl-none bg-white px-4 py-2.5 text-sm text-midnight shadow-sm">
                Which AI feature are you interested in?
              </div>
            </div>
            <AnimatePresence>
              {mode === null && (
                <motion.div
                  key="mode-buttons"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="ml-10 flex flex-col items-start gap-3">
                  <button
                    type="button"
                    onClick={() => onSelectMode("chat")}
                    className="flex items-center gap-3 text-left text-sm font-medium text-midnight transition hover:opacity-80">
                    <QuestionMarkCircleIcon className="h-5 w-5 shrink-0 text-lac-leman" />
                    {CHAT_MODE_INFO.chat.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelectMode("chat_article")}
                    className="flex items-center gap-3 text-left text-sm font-medium text-midnight transition hover:opacity-80">
                    <BookOpenIcon className="h-5 w-5 shrink-0 text-[#1ECDB9]" />
                    {CHAT_MODE_INFO.chat_article.label}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence initial={false} mode="popLayout">
              {[
                ...messages.map(msg => (
                  <motion.div
                    key={msg.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}>
                    <MessageBubble message={msg} mode={mode} />
                  </motion.div>
                )),
                isSending && (
                  <motion.div
                    key="typing"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}>
                    <TypingIndicator mode={mode} />
                  </motion.div>
                ),
              ]}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input bar */}
      <AnimatePresence initial={false}>
        {showInput && (
          <motion.div
            key="input-bar"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.35, ease: "easeInOut" },
            }}
            className="overflow-hidden border-t border-gray bg-white">
            <div className="m-4 flex overflow-hidden rounded-xl border border-gray">
              <textarea
                rows={1}
                value={inputText}
                onChange={e => onInputChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="What would you like to explore?"
                ref={inputRef}
                className="flex-1 resize-none bg-transparent px-4 py-3.5 text-sm text-midnight outline-none placeholder:text-gray-60"
                style={{ maxHeight: "96px" }}
              />
              <button
                type="button"
                onClick={onSend}
                disabled={isSendButtonDisabled || !inputText.trim()}
                className="flex shrink-0 items-center justify-center self-stretch bg-lac-leman px-5 text-white transition hover:brightness-110 disabled:opacity-40"
                aria-label="Send message">
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
